import { useTranslation } from 'react-i18next';
import { ReferenceDataEntry, type ReferenceCell, type ReferenceDataValue } from 'entities/document';
import { useRequiredParams } from 'app/router/params';
import { useState, useEffect, useMemo, useRef } from 'react';
import Tippy from '@tippyjs/react';
import { useQueryClient } from '@tanstack/react-query';
import { DropdownList } from 'react-widgets';
import { v4 as uuidv4 } from 'uuid';
import { getSelectedCells, isStylingSelected, getSelectedDropdownValue, referenceDataKeys } from 'entities/reference-data';
import { useReferenceData, useUpdateReferenceData, useUpdateReferenceTableCssClass } from 'entities/reference-data';
import { useGetDocument, shadowedCss } from 'entities/document';
import { useUser } from 'app/state/contexts/UserContext';
import { Spinner } from 'app/components';
import { StyledDataSheet } from 'app/components';
import { toolbarCSSExtractor, customClassExtractor } from 'shared/lib/css';
import { withCellStyling } from 'shared/lib/cell-styling';
import { parseCellOption, resolveCellStyling, type CellStyling } from 'shared/lib/cell-format';
import { usePermissions } from 'app/api/permission-queries';

interface DropdownOption {
  id: number;
  value: string;
  label: string;
}

interface ClassObj {
  className: string;
  labelEn?: string;
  labelSv?: string;
}

interface ToolbarOption {
  selector: string;
  title: string;
}

interface CellStylingOption {
  label: string;
  reset?: boolean;
  element?: 'td' | 'th';
  className?: string;
}

interface CellSelection {
  start?: { i: number; j: number };
  end?: { i: number; j: number };
}

interface DetailsTableProps {
  viewLocale?: string;
}

export function DetailsTable(props: DetailsTableProps) {
  const { t } = useTranslation(['common', 'reference-data', 'reference-table']);

  const { documentId, refId } = useRequiredParams('documentId', 'refId');
  const { selectedOrganization } = useUser();
  const { data: document } = useGetDocument(documentId, selectedOrganization);
  const docCss = shadowedCss(document?.css);
  const exposedCss = document?.exposedCss;
  const tableLocale = document?.content.referenceData.find((rd: ReferenceDataEntry) => rd.superId === refId)?.locale ?? document?.content.locale;
  const effectiveLocale = props.viewLocale ?? tableLocale;
  const showOriginalValue = effectiveLocale === tableLocale;

  const { documentPermissions } = usePermissions(documentId);
  const { mutate: updateReferenceData } = useUpdateReferenceData();
  const referenceData = useReferenceData(refId, documentId);
  const { data } = referenceData;
  const queryClient = useQueryClient();

  const normalCellLabel = t('reference-data:data-table.normal-cell');
  const tableHeadLabel = t('reference-data:data-table.table-head');

  const elementOptions: CellStylingOption[] = [
    { reset: true, label: normalCellLabel },
    { element: 'th', label: tableHeadLabel },
  ];

  const builtinCellClasses: CellStylingOption[] = [
    { className: 'table--heading', label: t('reference-data:data-table.table-title') },
    { className: 'table--part-sum', label: t('reference-data:data-table.part-sum') },
    { className: 'table--sum', label: t('reference-data:data-table.sum') },
  ];

  const borderFormats = [
    { id: 1, value: 'clear', label: t('reference-data:data-table.no-border') },
    { id: 2, value: 'table--border-top', label: t('reference-data:data-table.border-top') },
    { id: 3, value: 'table--border-bottom', label: t('reference-data:data-table.border-bottom') },
    { id: 4, value: 'table--border-left', label: t('reference-data:data-table.border-left') },
    { id: 5, value: 'table--border-right', label: t('reference-data:data-table.border-right') },
    { id: 6, value: 'table--border-top-bottom', label: t('reference-data:data-table.border-top-bottom') },
    { id: 7, value: 'table--border-left-right', label: t('reference-data:data-table.border-left-right') },
    { id: 8, value: 'table--border-all', label: t('reference-data:data-table.border-all') },
  ];

  const backgrounds = [
    { id: 1, value: 'clear', label: t('reference-data:data-table.no-background') },
    { id: 2, value: 'table--background-primary', label: t('reference-data:data-table.primary-background') },
    { id: 3, value: 'table--background-secondary', label: t('reference-data:data-table.secondary-background') },
    { id: 4, value: 'table--background-tertiary', label: t('reference-data:data-table.tertiary-background') },
    { id: 5, value: 'table--background-quaternary', label: t('reference-data:data-table.quaternary-background') },
  ];

  const { user } = useUser();
  const lang = user?.userProperties?.cultureId ?? 'sv';

  const { classes: availableTableClassObjects, error: tableClassParseError } = customClassExtractor(docCss, 'table');
  const availableTableClasses = availableTableClassObjects.map((c: ClassObj) => c.className);
  const tableClassLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const c of availableTableClassObjects) {
      map[c.className] = lang === 'en' ? c.labelEn : c.labelSv;
    }
    return map;
  }, [availableTableClassObjects, lang]);
  const { mutate: updateCssClass } = useUpdateReferenceTableCssClass();

  const tableCssClasses = useMemo(() => {
    const cssClass = document?.content?.referenceData?.find((rd: ReferenceDataEntry) => rd.superId === refId)?.cssClass || '';
    return cssClass
      .split(',')
      .map((c: string) => c.trim())
      .filter(Boolean);
  }, [document, refId]);

  const handleAddTableClass = (cls: string) => {
    if (!tableCssClasses.includes(cls)) {
      const updated = [...tableCssClasses, cls];
      updateCssClass({ documentSuperId: documentId, referenceDataSuperId: refId, cssClass: updated.join(', ') });
    }
  };

  const handleRemoveTableClass = (cls: string) => {
    const updated = tableCssClasses.filter((c: string) => c !== cls);
    updateCssClass({ documentSuperId: documentId, referenceDataSuperId: refId, cssClass: updated.join(', ') });
  };

  const unselectedTableClasses = availableTableClasses.filter((cls: string) => !tableCssClasses.includes(cls));

  const [selection, setSelection] = useState<CellSelection | null>(null);
  const [cellStylingOpen, setCellStylingOpen] = useState(false);
  const keepCellStylingOpen = useRef(false);

  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    setIsDirty(false);
  }, [refId]);

  const getDropdownSelector = (type: string, options: DropdownOption[]) => {
    return (data: unknown, selection: CellSelection | null) => getSelectedDropdownValue(type, data as ReferenceDataValue, selection, options);
  };

  const financialToolbars = toolbarCSSExtractor(docCss, lang, 'financial');

  // Financial-toolbar entries are selectors: ".sum", "td.td-75px" or a bare "th". The
  // element half, when present, moves the cell; the class half is appended.
  const cellClassOptions: CellStylingOption[] = [...builtinCellClasses];
  const seenOptions = new Set(cellClassOptions.map((o) => o.className));
  for (const option of (financialToolbars[0]?.options ?? []) as ToolbarOption[]) {
    const { element, className } = parseCellOption(option.selector);
    const key = className || element;
    if (!key || seenOptions.has(key)) continue;
    seenOptions.add(key);
    cellClassOptions.push({ className, element, label: option.title });
  }

  const cellClassLabel = (className: string) => cellClassOptions.find((o) => o.className === className)?.label ?? className;

  // One dropdown drives both slots: the two element entries behave like radios, the
  // class entries toggle independently.
  const cellStylingOptions = [...elementOptions, ...cellClassOptions];

  const getSelectedBorder = getDropdownSelector('border', borderFormats);
  const getSelectedBackground = getDropdownSelector('background', backgrounds);

  const applyToSelectedCells = (updateCell: (cell: ReferenceCell) => void) => {
    if (!selection) return;

    const selectedCells = getSelectedCells(selection);
    if (!selectedCells.length) return;

    queryClient.setQueryData(referenceDataKeys.detail(documentId, refId), (old?: ReferenceDataValue) => {
      if (!old?.selectionContent) return old;

      const grid = [...old.selectionContent];
      const clonedRows = new Set<number>();

      selectedCells.forEach(({ x, y }) => {
        if (!grid[x]?.[y]) return;

        if (!clonedRows.has(x)) {
          grid[x] = [...grid[x]];
          clonedRows.add(x);
        }

        const cell = { ...grid[x][y] };
        updateCell(cell);
        grid[x][y] = cell;
      });

      return { ...old, selectionContent: grid };
    });

    setIsDirty(true);
  };

  const handleClick = (styling: string) => {
    const isActive = isStylingSelected(styling, data, selection) === true;

    applyToSelectedCells((cell) => {
      const next = withCellStyling(cell.styling, styling, !isActive);

      if (next) {
        cell.styling = next;
      } else {
        delete cell.styling;
      }
    });
  };

  const setStyle = (key: string, value: string) => {
    applyToSelectedCells((cell) => {
      cell[key] = value;
    });
  };

  const clearStyle = (key: string) => {
    applyToSelectedCells((cell) => {
      delete cell[key];
    });
  };

  // Writes `classes` + an element-only `format` back, folding any class a legacy row
  // still carries inside `format` into `classes`.
  const updateSelectedCells = (update: (current: CellStyling) => { classes: string[]; isHead: boolean }) => {
    applyToSelectedCells((cell) => {
      const { classes, isHead } = update(resolveCellStyling(cell));

      if (classes.length > 0) {
        cell.classes = classes;
      } else {
        delete cell.classes;
      }
      if (isHead) {
        cell.format = 'thead';
      } else {
        delete cell.format;
      }
    });
  };

  const selectedCells = getSelectedCells(selection).map(({ x, y }) => resolveCellStyling(data?.selectionContent?.[x]?.[y]));

  // Classes on every selected cell, and the shared element (null when mixed).
  const selectedCellClasses = selectedCells.length ? selectedCells[0].classes.filter((c) => selectedCells.every((cell) => cell.classes.includes(c))) : [];
  const selectedIsHead = !selectedCells.some((cell) => cell.isHead) ? false : selectedCells.every((cell) => cell.isHead) ? true : null;

  const isOptionSelected = (option: CellStylingOption) => {
    if (selectedCells.length === 0) return false;
    if (option.reset) return selectedIsHead === false && selectedCellClasses.length === 0;
    if (option.className) return selectedCellClasses.includes(option.className);
    return selectedIsHead === true;
  };

  const handleToggleOption = (option: CellStylingOption) => {
    if (option.reset) {
      updateSelectedCells(() => ({ classes: [], isHead: false }));
      return;
    }

    const { className } = option;
    if (!className) {
      const nextIsHead = selectedIsHead !== true;
      updateSelectedCells(({ classes }) => ({ classes, isHead: nextIsHead }));
      return;
    }

    if (selectedCellClasses.includes(className)) {
      updateSelectedCells(({ classes, isHead }) => ({ classes: classes.filter((c) => c !== className), isHead }));
      return;
    }

    updateSelectedCells(({ classes, isHead }) => ({
      classes: [...classes, className],
      // An element-qualified selector only matches when the cell is that element, so
      // applying it moves the cell too. A class-only selector leaves it alone.
      isHead: option.element ? option.element === 'th' : isHead,
    }));
  };

  const selectedSummary = [...(selectedIsHead ? [tableHeadLabel] : []), ...selectedCellClasses.map(cellClassLabel)];
  const cellStylingValue = { label: selectedSummary.length > 0 ? selectedSummary.join(', ') : normalCellLabel };

  const handleBackgroundChange = (menuItem: DropdownOption) => {
    if (menuItem.value === 'clear') {
      clearStyle('background');
    } else {
      setStyle('background', menuItem.value);
    }
  };

  const handleBorderChange = (menuItem: DropdownOption) => {
    if (menuItem.value === 'clear') {
      clearStyle('border');
    } else {
      setStyle('border', menuItem.value);
    }
  };

  const handleSubmit = () => {
    const referenceData = {
      name: data.name,
      documentSuperId: documentId,
      fileRevisionId: data.fileRevisionId,
      fileSuperId: data.fileSuperId,
      selection: data.selection,
      selectionContent: data.selectionContent,
      superId: data.superId,
      revisionId: uuidv4(),
    };

    // Cleared on success only: a failed save leaves the edits pending, so the
    // warning has to stay up.
    updateReferenceData({ document, referenceData }, { onSuccess: () => setIsDirty(false) });
  };

  if (!referenceData.isSuccess) {
    return <Spinner />;
  }

  return (
    <div>
      {documentPermissions.includes('DB') && (availableTableClasses.length > 0 || tableClassParseError) && (
        <div className="d-flex align-items-center flex-wrap gap-2 px-2 py-2">
          {tableClassParseError && (
            <Tippy content={<p className="gaby-tooltip arrow-top">{tableClassParseError}</p>} placement="bottom" theme="light">
              <span className="text-warning">
                <i className="fa-regular fa-triangle-exclamation" />
              </span>
            </Tippy>
          )}
          {availableTableClasses.length > 0 && (
            <>
              <div className="rw-dropdown-wrapper" style={{ minWidth: '200px' }}>
                <DropdownList
                  data={unselectedTableClasses.map((cls: string) => ({ id: cls, value: cls, label: tableClassLabelMap[cls] || cls }))}
                  textField="label"
                  placeholder={t('reference-data:data-table.select-css-classes')}
                  onSelect={(item: { value: string }) => handleAddTableClass(item.value)}
                  value={null}
                  filter="contains"
                  selectIcon={<i className="fa-regular fa-chevron-down" />}
                  onChange={() => {}}
                />
              </div>
              {tableCssClasses.map((cls: string) => (
                <span key={cls} className="badge bg-blue-100 text-blue-700 d-inline-flex align-items-center px-2 py-1 rounded-pill">
                  {cls}
                  <button
                    type="button"
                    className="bg-transparent border-0 p-0 ms-1"
                    onClick={() => handleRemoveTableClass(cls)}
                    style={{ fontSize: '0.6rem', lineHeight: 1 }}
                  >
                    <i className="fa-regular fa-xmark" />
                  </button>
                </span>
              ))}
            </>
          )}
        </div>
      )}
      <div className="reference-data--toolbar">
        <div className="rw-dropdown-wrapper" style={{ display: 'flex', flexGrow: 1 }}>
          <DropdownList<CellStylingOption>
            data={cellStylingOptions}
            selectIcon={<i className="fa-regular fa-chevron-down" />}
            textField="label"
            value={cellStylingValue}
            open={cellStylingOpen}
            onToggle={(open) => {
              if (!open && keepCellStylingOpen.current) return;
              setCellStylingOpen(open);
            }}
            onSelect={(option) => {
              // react-widgets closes twice per select (change + select); swallow both.
              keepCellStylingOpen.current = true;
              setTimeout(() => {
                keepCellStylingOpen.current = false;
              });
              handleToggleOption(option as CellStylingOption);
            }}
            renderListItem={({ item }) => (
              <span className="d-flex align-items-center gap-2">
                <i className={`fa-regular fa-check ${isOptionSelected(item) ? '' : 'invisible'}`} />
                {item.label}
              </span>
            )}
            onChange={() => {}}
          />
        </div>
        <div className="divider" />
        <div className="rw-dropdown-wrapper">
          <DropdownList
            data={borderFormats}
            selectIcon={<i className="fa-regular fa-chevron-down" />}
            textField="label"
            defaultValue={borderFormats[0]}
            value={getSelectedBorder(data, selection)}
            onSelect={handleBorderChange}
            onChange={() => {}}
          />
        </div>
        <div className="rw-dropdown-wrapper">
          <DropdownList
            data={backgrounds}
            selectIcon={<i className="fa-regular fa-chevron-down" />}
            textField="label"
            defaultValue={backgrounds[0]}
            value={getSelectedBackground(data, selection)}
            onSelect={handleBackgroundChange}
            onChange={() => {}}
          />
        </div>
        <div className="divider" />
        <div className="py-1 d-flex align-items-center">
          <Tippy content={<p className="gaby-tooltip arrow-top">{t('reference-table:bold')}</p>} placement="bottom" theme="light">
            <button
              onClick={() => {
                handleClick('bold');
              }}
              className={`${isStylingSelected('bold', data, selection) ? 'active' : ''}`}
            >
              <i className="fa-regular fa-bold" />
            </button>
          </Tippy>
          <Tippy content={<p className="gaby-tooltip arrow-top">{t('reference-table:italic')}</p>} placement="bottom" theme="light">
            <button
              onClick={() => {
                handleClick('italic');
              }}
              className={`${isStylingSelected('italic', data, selection) ? 'active' : ''}`}
            >
              <i className="fa-regular fa-italic" />
            </button>
          </Tippy>
          <Tippy content={<p className="gaby-tooltip arrow-top">{t('reference-table:underline')}</p>} placement="bottom" theme="light">
            <button
              onClick={() => {
                handleClick('underline');
              }}
              className={`${isStylingSelected('underline', data, selection) ? 'active' : ''}`}
            >
              <i className="fa-regular fa-underline" />
            </button>
          </Tippy>
        </div>
        <div className="divider" />
        <Tippy content={<p className="gaby-tooltip arrow-top">{t('reference-table:link-note')}</p>} placement="bottom" theme="light">
          <button>{t('reference-table:link-note')}</button>
        </Tippy>
        <div className="divider" />
        <Tippy
          content={
            <p className="gaby-tooltip arrow-bottom-attached" role="status">
              {t('reference-table:unsaved-changes')}
            </p>
          }
          placement="top"
          theme="light"
          visible={isDirty}
        >
          <span className="d-inline-flex">
            <Tippy content={<p className="gaby-tooltip arrow-top">{t('common:save')}</p>} placement="bottom" theme="light">
              <button onClick={handleSubmit}>{t('common:save')}</button>
            </Tippy>
          </span>
        </Tippy>
      </div>
      <div className="rd-table-scope" style={{ overflow: 'auto' }}>
        <style>{exposedCss}</style>
        <style>{`@scope (.rd-table-scope) { ${docCss} .page-content { height: auto !important; overflow: visible !important; width: auto !important; display: block !important; background: none !important; } }`}</style>
        <div className="page-content">
          <StyledDataSheet
            data={data.selectionContent}
            selected={selection}
            onSelect={setSelection}
            locale={effectiveLocale}
            showOriginalValue={showOriginalValue}
            cssClass={tableCssClasses.join(' ')}
          />
        </div>
      </div>
    </div>
  );
}
